import React, { useState, useEffect } from 'react';
import BookingModal from './BookingModal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [locations, setLocations] = useState([]);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  useEffect(() => {
    fetch('http://localhost:5000/api/locations')
      .then(res => res.json())
      .then(data => setLocations(data))
      .catch(err => console.error('Error fetching locations:', err));
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare Formspree data for contact form
      const formspreeData = {
        name: formData.name,
        email: formData.email,
        _replyto: formData.email,
        phone: formData.phone,
        message: formData.message,
        
        // Custom subject for contact form
        _subject: `New Contact Form Submission from ${formData.name}`,
        
        // Form type identifier
        form_type: 'contact_form'
      };

      const response = await fetch(process.env.REACT_APP_FORMSPREE_CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formspreeData)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSubmitStatus(''), 3000);
      } else {
        setSubmitStatus('error');
        console.error('Formspree error:', result);
        setTimeout(() => setSubmitStatus(''), 3000);
      }
    } catch (err) {
      setSubmitStatus('error');
      console.error('Contact form error:', err);
      setTimeout(() => setSubmitStatus(''), 3000);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 bg-charcoal relative">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-radial from-gold-400/20 to-transparent rounded-full translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-radial from-gold-400/20 to-transparent rounded-full -translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="max-w-7xl mx-auto relative">
        <div className="text-center mb-16">
          <span className="font-montserrat text-xs tracking-super-wide text-gold-400 uppercase">Get in Touch</span>
          <h2 className="font-playfair text-5xl md:text-6xl text-white mt-4 mb-6">
            Begin Your Journey
          </h2>
          <div className="w-24 h-px bg-gradient-gold mx-auto"></div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <p className="font-cormorant text-2xl text-white/90 mb-8">
              Let us help you create the perfect engagement ring or personalized jewelry piece for your special moments. From Valentine's Day and Mother's Day gifts to anniversary rings and graduation jewelry, we're here for all your milestone celebrations. Proudly serving North Georgia families for over 40 years.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 bg-transparent border-0 border-b border-gold-400/30 text-white placeholder-white/50 focus:border-gold-400 focus:outline-none transition-colors duration-300"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-0 py-3 bg-transparent border-0 border-b border-gold-400/30 text-white placeholder-white/50 focus:border-gold-400 focus:outline-none transition-colors duration-300"
                  />
                </div>
              </div>
              
              <div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-gold-400/30 text-white placeholder-white/50 focus:border-gold-400 focus:outline-none transition-colors duration-300"
                />
              </div>
              
              <div>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your vision..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className="w-full px-0 py-3 bg-transparent border-0 border-b border-gold-400/30 text-white placeholder-white/50 focus:border-gold-400 focus:outline-none transition-colors duration-300 resize-none"
                ></textarea>
              </div>
              
              <button
                type="submit"
                className="w-full py-4 bg-gradient-gold text-white font-montserrat text-sm tracking-wider hover:scale-[1.02] transition-transform duration-300"
              >
                SEND MESSAGE
              </button>
              
              {submitStatus === 'success' && (
                <p className="text-gold-400 text-center font-montserrat animate-fade-in">
                  Thank you for your message. We'll be in touch soon.
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-center font-montserrat animate-fade-in">
                  Something went wrong. Please try again.
                </p>
              )}
            </form>
          </div>
          
          <div className="lg:pl-12">
            <h3 className="font-playfair text-3xl text-white mb-8">
              Visit Our Locations
            </h3>
            
            <div className="space-y-8">
              {locations.map((location) => (
                <div key={location.id} className="border-l-2 border-gold-400/30 pl-6 hover:border-gold-400 transition-colors duration-300">
                  <h4 className="font-playfair text-2xl text-gold-400 mb-3">
                    {location.name}
                  </h4>
                  <p className="font-montserrat text-white/70 mb-2">{location.address}</p>
                  <p className="font-montserrat text-white/70 mb-2">{location.phone}</p>
                  <p className="font-montserrat text-sm text-white/50">{location.hours}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-12">
              <h4 className="font-playfair text-2xl text-white mb-6">
                Private Consultations
              </h4>
              <p className="font-montserrat text-white/70 mb-6">
                Schedule an exclusive consultation with our master craftsman 
                to design your custom engagement ring, discuss diamond options, 
                or create personalized jewelry for birthdays, anniversaries, or holiday gifts.
              </p>
              <button 
                onClick={() => setIsBookingModalOpen(true)}
                className="px-8 py-3 border border-gold-400 text-gold-400 font-montserrat text-sm tracking-wider hover:bg-gold-400 hover:text-charcoal transition-all duration-300"
              >
                BOOK APPOINTMENT
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        isFromRepair={false}
      />
    </section>
  );
};

export default Contact;